import { getContactRowById } from "../contacts/queries.ts";
import type { AffinityDb } from "../database.ts";
import {
  legacyIdentityKey,
  normalizeIdentityKey,
} from "../identities/normalize.ts";
import type { AffinityError } from "../lib/errors/affinity_error.ts";
import { isAffinityError } from "../lib/errors/is_affinity_error.ts";
import type { ContactRow } from "../lib/types/contact_row.ts";
import type { LinkRow } from "../lib/types/link_row.ts";
import { getLinkRowById } from "../links/queries.ts";
import { toContactEntityRef, toLinkEntityRef } from "./tool_ref.ts";
import {
  type ToolFailure,
  type ToolNeedsClarification,
  type ToolResult,
  toolFailure,
  toolNeedsClarification,
} from "./tool_types.ts";

export type ContactLocator =
  | { contactId: number }
  | { identity: { type: string; value: string } };

export type LinkLocator =
  | { linkId: number }
  | {
      endpoints: {
        fromContactId: number;
        toContactId: number;
        kind?: string | undefined;
        role?: string | null | undefined;
        isStructural?: boolean | undefined;
      };
    };

export type ResolvedTarget<T> =
  | { ok: true; value: T }
  | { ok: false; result: ToolFailure | ToolNeedsClarification };

export function resolveContactLocator(
  db: AffinityDb,
  locator: ContactLocator,
  field = "contact",
  allowMerged = false,
): ResolvedTarget<ContactRow> {
  if (!locator || typeof locator !== "object")
    return {
      ok: false,
      result: toolNeedsClarification(
        "missing_required_choice",
        `Provide ${field} locator.`,
        [field],
      ),
    };
  if ("contactId" in locator) {
    const row = getContactRowById(db, locator.contactId);
    if (!row) {
      return {
        ok: false,
        result: toolFailure(
          "domain",
          "not_found",
          "Contact not found.",
          `No live contact exists for \`${field}.contactId = ${locator.contactId}\`.`,
        ),
      };
    }
    if (!allowMerged && row.merged_into_contact_id !== null) {
      const winner = getContactRowById(db, row.merged_into_contact_id);
      const message = `Contact #${row.id} was merged into #${row.merged_into_contact_id} ${winner?.name ?? ""}; use contactId ${row.merged_into_contact_id}`;
      return {
        ok: false,
        result: toolFailure("domain", "conflict", message, message),
      };
    }
    return { ok: true, value: row };
  }

  if (!("identity" in locator) || !locator.identity)
    return {
      ok: false,
      result: toolNeedsClarification(
        "missing_required_choice",
        `Provide ${field}.contactId or identity.`,
        [field],
      ),
    };
  const normalizedKey = normalizeIdentityKey(
    locator.identity.type,
    locator.identity.value,
  );
  const row = db
    .prepare(
      `SELECT c.id, c.name, c.kind, c.lifecycle_state, c.is_owner,
              c.merged_into_contact_id, c.created_at, c.updated_at, c.deleted_at
       FROM identities i
       INNER JOIN contacts c ON c.id = i.contact_id
       WHERE i.removed_at IS NULL
         AND c.deleted_at IS NULL
         AND (i.normalized_key IN (?, ?) OR (i.type = 'custom:atproto-did' AND ? = 'did:' || lower(i.value)))
       LIMIT 2`,
    )
    .all(
      normalizedKey,
      legacyIdentityKey(normalizedKey),
      normalizedKey,
    ) as unknown as ContactRow[];
  if (row.length === 0) {
    return {
      ok: false,
      result: toolFailure(
        "domain",
        "not_found",
        "Contact not found.",
        `No live contact matches identity \`${locator.identity.type}:${locator.identity.value}\`.`,
      ),
    };
  }
  if (row.length > 1) {
    return {
      ok: false,
      result: toolNeedsClarification(
        "multiple_plausible_matches",
        "More than one contact matches that identity locator.",
        [field],
        {
          entities: row.map(toContactEntityRef),
          options: row.map((contact) => ({
            label: `${contact.name} (#${contact.id})`,
            value: contact.id,
          })),
        },
      ),
    };
  }
  const matchedContact = row[0];
  if (matchedContact === undefined) {
    return {
      ok: false,
      result: toolFailure(
        "system",
        "system_error",
        "Contact resolution failed.",
        "Expected one contact match but none could be loaded.",
      ),
    };
  }
  return { ok: true, value: matchedContact };
}

export function resolveLinkLocator(
  db: AffinityDb,
  locator: LinkLocator,
  field = "link",
): ResolvedTarget<LinkRow> {
  if (!locator || typeof locator !== "object")
    return {
      ok: false,
      result: toolNeedsClarification(
        "missing_required_choice",
        `Provide ${field} locator.`,
        [field],
      ),
    };
  if ("linkId" in locator) {
    const row = getLinkRowById(db, locator.linkId);
    if (!row) {
      return {
        ok: false,
        result: toolFailure(
          "domain",
          "not_found",
          "Link not found.",
          `No live link exists for \`${field}.linkId = ${locator.linkId}\`.`,
        ),
      };
    }
    return { ok: true, value: row };
  }

  if (!("endpoints" in locator) || !locator.endpoints)
    return {
      ok: false,
      result: toolNeedsClarification(
        "missing_required_choice",
        `Provide ${field}.linkId or endpoints.`,
        [field],
      ),
    };
  const { endpoints } = locator;
  const clauses = [
    "removed_at IS NULL",
    "from_contact_id = ?",
    "to_contact_id = ?",
  ];
  const params: Array<number | string> = [
    endpoints.fromContactId,
    endpoints.toContactId,
  ];
  if (endpoints.kind !== undefined) {
    clauses.push("kind = ?");
    params.push(endpoints.kind);
  }
  if (endpoints.role !== undefined) {
    clauses.push("COALESCE(role, '') = ?");
    params.push(endpoints.role ?? "");
  }
  if (endpoints.isStructural !== undefined) {
    clauses.push("is_structural = ?");
    params.push(endpoints.isStructural ? 1 : 0);
  }
  const rows = db
    .prepare(
      `SELECT id, from_contact_id, to_contact_id, kind, role, is_structural,
              rank, affinity, trust, state, cadence_days, bond,
              created_at, updated_at, removed_at
       FROM links
       WHERE ${clauses.join(" AND ")}
       ORDER BY id ASC
       LIMIT 10`,
    )
    .all(...params) as unknown as LinkRow[];
  if (rows.length === 0) {
    return {
      ok: false,
      result: toolFailure(
        "domain",
        "not_found",
        "Link not found.",
        "No live link matches the supplied endpoint locator.",
      ),
    };
  }
  if (rows.length > 1) {
    return {
      ok: false,
      result: toolNeedsClarification(
        "ambiguous_target",
        "More than one live link matches those endpoints.",
        [field],
        {
          entities: rows.map(toLinkEntityRef),
          options: rows.map((link) => ({
            label: `${link.kind} #${link.id}`,
            value: link.id,
          })),
        },
      ),
    };
  }
  const matchedLink = rows[0];
  if (matchedLink === undefined) {
    return {
      ok: false,
      result: toolFailure(
        "system",
        "system_error",
        "Link resolution failed.",
        "Expected one link match but none could be loaded.",
      ),
    };
  }
  return { ok: true, value: matchedLink };
}

export function mapErrorToToolFailure(
  error: unknown,
  summary = "The tool failed.",
): ToolFailure {
  if (
    error &&
    typeof error === "object" &&
    "ok" in error &&
    error.ok === false &&
    "outcome" in error &&
    error.outcome === "error"
  )
    return error as ToolFailure;
  if (isAffinityError(error)) {
    const result = toolFailure(
      "domain",
      affinityErrorCodeToToolCode(error),
      summary,
      error.message,
    );
    const id = (error as { existingContactId?: number }).existingContactId;
    if (id !== undefined) {
      result.data = { existingContactId: id };
      result.error.details = { existingContactId: id };
    }
    return result;
  }
  if (error instanceof Error) {
    return toolFailure("system", "system_error", summary, error.message);
  }
  return toolFailure(
    "system",
    "system_error",
    summary,
    "Unknown system error.",
  );
}

export function withToolHandling<TData>(
  operation: () => ToolResult<TData>,
  summary = "The tool failed.",
): ToolResult<TData> {
  try {
    return operation();
  } catch (error) {
    return mapErrorToToolFailure(error, summary);
  }
}

function affinityErrorCodeToToolCode(
  error: AffinityError,
): ToolFailure["error"]["code"] {
  switch (error.code) {
    case "NOT_FOUND":
      return "not_found";
    case "CONFLICT":
      return "conflict";
    case "VALIDATION":
      return "invalid_input";
    case "STATE":
      return "invalid_state";
    case "MERGE":
      return "constraint_violation";
    case "INVARIANT":
      return "constraint_violation";
    default:
      return "system_error";
  }
}
